import type { QuickRebuttal, SalesChecklist } from "@expert/contentful";

import { Box, Button, Stack, Text } from "@mantine/core";

import { LightBulbIcon } from "@/sales-tools/home-product/components/Icons";
import styles from "@/sales-tools/home-product/components/StepSpecific/SalesFooter.module.css";
import { useProductSaleFlow } from "@/sales-tools/product-sale-flow/useProductSaleFlow";
import { useLogger } from "@/sdk";

import type { EligibleProduct } from "../../stores";

import { SaleProgress } from "../../types";
import { FullOfferChecklist } from "../FullOfferChecklist/FullOfferChecklist";
import { SalesFooter } from "./SalesFooter";

interface FullOfferChecklistStepProps {
    product: EligibleProduct;
    salesChecklist?: SalesChecklist;
    quickRebuttal?: QuickRebuttal;
}

export function FullOfferChecklistStep({ product, salesChecklist, quickRebuttal }: FullOfferChecklistStepProps) {
    const logger = useLogger({ module: "FullOfferChecklistStep", supportTeam: "sales" });
    const { addQuickRebuttalToExpertAssistTimeline } = useProductSaleFlow();

    const QuickRebuttalButton = () => {
        return (
            <Button
                className={styles.quickRebuttalButton}
                variant="outline"
                radius={"lg"}
                fz={"xs"}
                size={"sm"}
                leftSection={<LightBulbIcon />}
                onClick={() => addQuickRebuttalToExpertAssistTimeline(quickRebuttal)}
                color="white"
            >
                Quick rebuttal
            </Button>
        );
    };

    if (!salesChecklist) {
        logger.error({ product, salesChecklist }, "Full offer checklist failed to render");
    }

    return (
        <Stack pos="relative" flex={1}>
            <Box
                flex={1}
                style={{
                    overflowY: "auto",
                }}
            >
                {product.saleProgress === SaleProgress.InProgress && salesChecklist ? (
                    <FullOfferChecklist salesChecklist={salesChecklist} />
                ) : (
                    <Text mt="20%" c="dark.1" size="sm">
                        Offer checklist is currently unavailable. Try again later
                    </Text>
                )}
            </Box>

            <SalesFooter product={product} buttonSlot={quickRebuttal && <QuickRebuttalButton />} />
        </Stack>
    );
}
